import * as React from 'react';
import type { FormStructure } from '@europrocurement/flexy-form';
import { useTheme } from '@mui/material';
import { SectionOverlay } from '@europrocurement/flexy-components';

export type SectionLockConfig = {
    sectionId: string;
    sectionFields: FormStructure[];
    isLocked: boolean;
    lockMessage: string;
};

export type UseFormSectionLockOptions = {
    /**
     * Liste personnalisée des champs automatiques spécifiques à ce formulaire
     * Sera mergée avec DEFAULT_AUTO_FILLED_FIELDS
     */
    customAutoFilledFields?: string[];
    /**
     * Remplacer complètement la liste par défaut au lieu de la merger
     */
    replaceDefaultAutoFields?: boolean;
};

/**
 * Liste des champs automatiques qui doivent toujours rester disabled
 */
const DEFAULT_AUTO_FILLED_FIELDS = [
    'nom_journal',
    'date_parution',
    'nom_annonceur',
    'denomination_societe_formalite',
    'raison_sociale_fournisseur',
    'type_remise_fournisseur',
] as const;

export const useFormSectionLock = function (options: UseFormSectionLockOptions = {}) {
    const { customAutoFilledFields = [], replaceDefaultAutoFields = false } = options;

    /**
     * Liste finale des champs automatiques à toujours garder disabled
     */
    const autoFilledFields = React.useMemo(() => {
        if (replaceDefaultAutoFields) {
            return customAutoFilledFields;
        }
        return [...DEFAULT_AUTO_FILLED_FIELDS, ...customAutoFilledFields];
    }, [customAutoFilledFields, replaceDefaultAutoFields]);

    /**
     * Applique les styles et propriétés de verrouillage à un champ
     */
    const applyLockPropertiesToField = React.useCallback(
        (field: FormStructure, sectionId: string, isLocked: boolean): FormStructure => {
            // Les champs automatiques restent toujours disabled
            if (autoFilledFields.includes(field.name)) {
                return {
                    ...field,
                    disabled: true,
                };
            }

            // Pour les autres champs, appliquer le disabled selon le verrouillage
            return {
                ...field,
                disabled: isLocked,
            };
        },
        [autoFilledFields],
    );

    const { palette } = useTheme();

    /**
     * Crée une section avec overlay de verrouillage
     */
    const createFormSection = React.useCallback(
        (config: SectionLockConfig): FormStructure[] => {
            const { sectionId, sectionFields, isLocked, lockMessage } = config;

            // Marqueur de section avec overlay
            const sectionMarker: FormStructure[] = [
                {
                    type: 'customItem',
                    name: `${sectionId}_overlay`,
                    renderField: () => (
                        <React.Suspense fallback={null}>
                            <SectionOverlay
                                sectionId={sectionId}
                                isLocked={isLocked}
                                lockMessage={lockMessage}
                                colors={{
                                    overlayBackground: palette.grey[200],
                                    lockIconBackground: palette.background.paper,
                                    lockIconColor: palette.primary.main,
                                    tooltipBackground: palette.grey[900],
                                    tooltipTextColor: palette.grey[100],
                                    lockIconShadow: palette.grey[900],
                                }}
                            />
                        </React.Suspense>
                    ),
                },
            ];

            // Application des propriétés de verrouillage aux champs
            const fieldsWithLockProperties = sectionFields.map((field) =>
                applyLockPropertiesToField(field, sectionId, isLocked),
            );

            return [...sectionMarker, ...fieldsWithLockProperties];
        },
        [applyLockPropertiesToField, palette.background.paper, palette.grey, palette.primary.main],
    );

    return {
        createFormSection,
        autoFilledFields,
    };
};
